import type React from 'react';
import { RowDrawerBarcodeFix } from './RowDrawerBarcodeFix';
import { RowDrawerCandidates } from './RowDrawerCandidates';
import { RowDrawerGroupValues } from './RowDrawerGroupValues';
import { RowDrawerVariantFix } from './RowDrawerVariantFix';
import type { RowDrawerModel, UseRowDrawerResult } from './useRowDrawer';

type RowDrawerResolutionProps = Pick<
    UseRowDrawerResult,
    | 'onPickCandidate'
    | 'onTakeGroupValues'
    | 'onPickVariantValue'
    | 'onApplyVariantValue'
    | 'onApplyBarcodeFix'
> & {
    model: RowDrawerModel;
};

/**
 * What resolves the row's problems, under the problem card's sentences
 * (`RowDrawerNotes`' `resolution`): the candidates to pick from
 * (`RowDrawerCandidates`), the group's rows to take values from
 * (`RowDrawerGroupValues`), the variant's figures to pick one value of
 * (`RowDrawerVariantFix`), and the IMEI move and the shared barcode's keeper
 * (`RowDrawerBarcodeFix`), each only when the row has that problem.
 */
export const RowDrawerResolution: React.FC<RowDrawerResolutionProps> = ({
    model,
    onPickCandidate,
    onTakeGroupValues,
    onPickVariantValue,
    onApplyVariantValue,
    onApplyBarcodeFix,
}) => (
    <>
        {model.candidates.length > 0 ? (
            <RowDrawerCandidates
                candidates={model.candidates}
                onPick={onPickCandidate}
            />
        ) : null}
        {model.groupMismatch === null ? null : (
            <RowDrawerGroupValues
                mismatch={model.groupMismatch}
                onTake={onTakeGroupValues}
                sku={model.sku}
            />
        )}
        {model.variantFix === null ? null : (
            <RowDrawerVariantFix
                fix={model.variantFix}
                onApply={onApplyVariantValue}
                onPick={onPickVariantValue}
            />
        )}
        {model.barcodeFixes.map((fix) => (
            <RowDrawerBarcodeFix
                fix={fix}
                key={fix.code}
                onApply={onApplyBarcodeFix}
            />
        ))}
    </>
);
